<script lang="ts">
	/**
	 * What is being bought, shown beside the question the checkout page is asking (Design §14).
	 *
	 * `/Checkout/Start` used to render nothing about the purchase at all — it was a spinner on the
	 * way to the provider, which is fine while it is a spinner and wrong the moment it stops to ask
	 * something. It now asks two things: an email address, from a customer who has no account, and
	 * a billing country. Both are questions put to somebody mid-purchase, and a page that asks for
	 * an email with no mention of what it is for is a page that reads like a newsletter box.
	 *
	 * So the plan and its price sit next to the field. The price above all: it is the fact the
	 * customer is deciding on, it was last seen on a screen they have since left, and being asked
	 * to hand over an address before seeing it again is exactly when a purchase is abandoned.
	 *
	 * **It states and never re-decides.** There is no control on it — the choice was made on the
	 * product page and confirmed in its inline checkout; this is the receipt of that decision,
	 * carried forward so it is still visible while the last question is answered.
	 *
	 * **It invents no total.** Tax is computed at the provider (§10) and a seat count multiplied by
	 * a unit price is exact for a percentage coupon and wrong for a fixed one — so the seat count
	 * and the coupon are *stated*, the unit price is rendered by the one component allowed to
	 * render money, and the arithmetic is left to the page that can do it honestly. A plausible
	 * figure out by a factor of the seat count is worse than no figure, because it is read as
	 * authoritative.
	 */

	import PriceDisplay from './PriceDisplay.svelte';
	import { mediaURL } from '../Utilities';
	import type { Price, Product, StorefrontCurrency } from '../AppTypes';

	export let product: Product | null = null;
	export let price: Price | null = null;
	/** The whole currency row, never a code: `amount / 100` is a hundred times wrong for JPY. */
	export let currency: StorefrontCurrency | undefined = undefined;
	/** Seats chosen for a per-seat price (§9.7), or null for a flat one. */
	export let seats: number | null = null;
	/** A code the customer applied, stated as applied and never priced here (§12). */
	export let couponCode: string | null = null;

	/*
	 * Seats or units (§4.1). Decided here rather than passed in, because this component re-reads the
	 * product itself — the intent carries identifiers, not a description — so it already holds the
	 * one fact that settles it, and a prop would be a second copy of it that could disagree.
	 */
	$: isUnitPurchase = product?.type === 'one_time';
	$: unitNoun = isUnitPurchase ? 'item' : 'user';
	$: unitNounPlural = isUnitPurchase ? 'items' : 'users';
</script>

{#if product !== null && price !== null}
	<div class="widget bg-depth space-y-4">
		<div class="card-label"><i class="fa-solid fa-basket-shopping text-[11px] mr-1" />Your purchase</div>

		<!-- What, before how much: the product is the thing being bought. -->
		<div class="flex items-start gap-3">
			{#if product.imageURL}
				<img src={mediaURL(product.imageURL)} alt="" class="w-11 h-11 rounded-xl object-contain shrink-0" />
			{/if}
			<div class="min-w-0">
				<div class="font-semibold leading-tight">{product.name}</div>
				{#if product.tagline}
					<div class="text-sm text-default">{product.tagline}</div>
				{/if}
			</div>
		</div>

		<PriceDisplay {price} {currency} currencyCode={product.currency} showTaxNote={false} />

		{#if seats !== null && price.isSeatBased}
			<p class="text-sm text-default">
				<i class={`fa-solid ${isUnitPurchase ? 'fa-layer-group' : 'fa-users'} opacity-70 mr-1`} />For
				{seats}
				{seats === 1 ? unitNoun : unitNounPlural}, charged per {unitNoun}.
			</p>
		{/if}

		{#if couponCode !== null && couponCode.length > 0}
			<!--
				Stated as applied, with no figure. §12 answers every coupon failure with one uniform
				body and never says which kind of discount it is, so a number here would be a guess —
				and a percentage and a fixed amount behave differently the moment seats are involved.
			-->
			<p class="text-sm text-default">
				<i class="fa-solid fa-ticket opacity-70 mr-1" />Discount code
				<strong class="text-gray-700 dark:text-gray-300">{couponCode.toUpperCase()}</strong> applied.
			</p>
		{/if}

		<p class="text-xs text-default">
			Tax and the final total are shown on the payment page.
		</p>
	</div>
{/if}
